import React, { useEffect, useState } from "react";
import { Instagram } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { openExternalUrl } from "@/hooks/useExternalLink";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Reveal from "./Reveal";

const INSTAGRAM_URL = "https://www.instagram.com/the.vent.app";

export interface InstagramPost {
  id: string;
  imageUrl: string | null;
  caption: string;
  permalink: string;
}

const PLACEHOLDERS: InstagramPost[] = [
  { id: "p1", imageUrl: null, caption: "Designed to help you let it out.", permalink: INSTAGRAM_URL },
  { id: "p2", imageUrl: null, caption: "You don't have to carry it quietly.", permalink: INSTAGRAM_URL },
  { id: "p3", imageUrl: null, caption: "Small check-ins count.", permalink: INSTAGRAM_URL },
  { id: "p4", imageUrl: null, caption: "Rest is not falling behind.", permalink: INSTAGRAM_URL },
  { id: "p5", imageUrl: null, caption: "Say the thing out loud.", permalink: INSTAGRAM_URL },
];

const InstagramCarousel = () => {
  const [posts, setPosts] = useState<InstagramPost[]>(PLACEHOLDERS);

  useEffect(() => {
    let cancelled = false;
    supabase.functions
      .invoke("instagram-feed")
      .then(({ data, error }) => {
        if (cancelled || error) return;
        const items = (data as { posts?: InstagramPost[] } | null)?.posts;
        if (items && items.length > 0) setPosts(items);
      })
      .catch(() => {
        /* keep placeholders */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="py-24 md:py-32 bg-paper" aria-labelledby="social-heading">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="max-w-2xl">
          <p className="font-display uppercase tracking-[0.3em] text-xs text-brand">Community</p>
          <h2
            id="social-heading"
            className="mt-4 font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight text-foreground"
          >
            See What We're Talking About
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-muted-foreground">
            The conversation keeps going beyond the app about stress, relationships, rest and
            everyday life. Follow along and join in.
          </p>
        </Reveal>
      </div>

      <Reveal className="mt-12">
        <Carousel opts={{ align: "start", dragFree: true }} className="w-full">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <CarouselContent className="-ml-4">
              {posts.map((post) => (
                <CarouselItem
                  key={post.id}
                  className="pl-4 basis-[78%] sm:basis-1/2 lg:basis-[30%]"
                >
                  <button
                    type="button"
                    onClick={() => openExternalUrl(post.permalink)}
                    className="group block w-full text-left"
                  >
                    <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg bg-mist">
                      {post.imageUrl ? (
                        <img
                          src={post.imageUrl}
                          alt={post.caption || "Instagram post from @the.vent.app"}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center p-8">
                          <p className="font-display uppercase text-2xl leading-tight tracking-tight text-ink/70">
                            {post.caption}
                          </p>
                        </div>
                      )}
                      <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background/85 text-foreground">
                        <Instagram className="h-4 w-4" />
                      </span>
                    </div>
                    {post.imageUrl && post.caption && (
                      <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">
                        {post.caption}
                      </p>
                    )}
                    <p className="mt-2 text-xs uppercase tracking-[0.2em] text-slate">
                      @the.vent.app
                    </p>
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>

            <div className="mt-8 flex items-center gap-3">
              <CarouselPrevious className="static translate-y-0" />
              <CarouselNext className="static translate-y-0" />
              <button
                type="button"
                onClick={() => openExternalUrl(INSTAGRAM_URL)}
                className="ml-auto inline-flex items-center gap-2 text-sm font-medium text-foreground underline-offset-4 hover:underline"
              >
                <Instagram className="h-4 w-4" />
                Follow @the.vent.app
              </button>
            </div>
          </div>
        </Carousel>
      </Reveal>
    </section>
  );
};

export default InstagramCarousel;
